import axios from 'axios';

import { parse } from '../../util/urlHelpers';
import { isOriginInUse } from '../../util/search';
import { apiBaseUrl } from '../../util/api';
import { searchListingsWithActionCreators } from '../SearchPage/SearchPage.duck';

export const FETCH_SALE_LISTINGS_REQUEST = 'app/SaleCampaignPage/FETCH_SALE_LISTINGS_REQUEST';
export const FETCH_SALE_LISTINGS_SUCCESS = 'app/SaleCampaignPage/FETCH_SALE_LISTINGS_SUCCESS';
export const FETCH_SALE_LISTINGS_ERROR = 'app/SaleCampaignPage/FETCH_SALE_LISTINGS_ERROR';

const RESULT_PAGE_SIZE = 24;
export const SALE_DISCOUNT_SORT_KEY = 'saleDiscount';
export const NEWEST_SORT_KEY = 'createdAt';

export const getCampaignDefaultSort = campaign =>
  campaign?.sortByDiscount === false ? NEWEST_SORT_KEY : SALE_DISCOUNT_SORT_KEY;

const initialState = {
  listings: [],
  pagination: null,
  facetCounts: {},
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  campaign: null,
  campaignListingPrices: {},
  campaignKey: null,
  campaignNotFound: false,
};

const resultIds = listings => listings.map(l => l.id);

export const createSaleCampaignPagination = (page, totalItems) => {
  const requestedPage = Number(page);
  const totalPages = Math.max(Math.ceil(totalItems / RESULT_PAGE_SIZE), 1);
  const currentPage =
    Number.isFinite(requestedPage) && requestedPage > 0
      ? Math.min(Math.floor(requestedPage), totalPages)
      : 1;

  return {
    page: currentPage,
    perPage: RESULT_PAGE_SIZE,
    totalItems,
    totalPages,
  };
};

export default function saleCampaignPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SALE_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        campaignKey: payload.searchParams?.campaignKey,
        campaignNotFound: false,
        searchInProgress: true,
        searchListingsError: null,
      };
    case FETCH_SALE_LISTINGS_SUCCESS:
      return {
        ...state,
        listings: payload.listings,
        currentPageResultIds: resultIds(payload.listings),
        pagination: payload.pagination,
        facetCounts: payload.facetCounts || {},
        campaign: payload.campaign || null,
        campaignListingPrices: payload.campaignListingPrices || {},
        campaignKey: payload.campaignKey || state.campaignKey,
        campaignNotFound: !!payload.campaignNotFound,
        searchInProgress: false,
      };
    case FETCH_SALE_LISTINGS_ERROR:
      return { ...state, searchInProgress: false, searchListingsError: payload };
    default:
      return state;
  }
}

const fetchSaleListingsRequest = searchParams => ({
  type: FETCH_SALE_LISTINGS_REQUEST,
  payload: { searchParams },
});

const fetchSaleListingsSuccess = ({
  listings,
  pagination,
  facetCounts,
  campaign,
  campaignListingPrices,
  campaignKey,
  campaignNotFound,
}) => ({
  type: FETCH_SALE_LISTINGS_SUCCESS,
  payload: {
    listings,
    pagination,
    facetCounts,
    campaign,
    campaignListingPrices,
    campaignKey,
    campaignNotFound,
  },
});

const fetchSaleListingsError = e => ({
  type: FETCH_SALE_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const loadData = (params, search, config) => {
  const campaignKey = params.campaignKey;

  return dispatch => {
    const emptyPagination = { page: 1, perPage: RESULT_PAGE_SIZE, totalItems: 0, totalPages: 1 };
    dispatch(fetchSaleListingsRequest({ campaignKey }));

    return axios
      .get(
        `${apiBaseUrl(
          process.env.REACT_APP_MARKETPLACE_ROOT_URL
        )}/api/sale-campaigns/${encodeURIComponent(campaignKey)}`,
        {
          params: { page: 1, perPage: 1 },
          withCredentials: true,
        }
      )
      .then(response => {
        const campaign = response.data?.campaign || null;
        const loadedCampaignKey = response.data?.campaignKey || campaignKey;
        const salePrices = response.data?.salePrices || {};

        if (!campaign) {
          dispatch(
            fetchSaleListingsSuccess({
              listings: [],
              pagination: emptyPagination,
              facetCounts: {},
              campaign,
              campaignListingPrices: salePrices,
              campaignKey: loadedCampaignKey,
              campaignNotFound: !campaign,
            })
          );
          return [];
        }

        const queryParams = parse(search, {
          latlng: ['origin'],
          latlngBounds: ['bounds'],
        });

        const { page = 1, origin, sort, ...rest } = queryParams;
        const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
        const defaultSort = getCampaignDefaultSort(campaign);
        const searchParams = {
          ...rest,
          ...originMaybe,
          campaignKey: loadedCampaignKey,
          saleCampaignKey: loadedCampaignKey,
          sort: sort || defaultSort,
          page,
          perPage: RESULT_PAGE_SIZE,
        };

        return dispatch(
          searchListingsWithActionCreators(searchParams, config, {
            request: fetchSaleListingsRequest,
            success: payload =>
              fetchSaleListingsSuccess({
                ...payload,
                campaign,
                campaignListingPrices: salePrices,
                campaignKey: loadedCampaignKey,
              }),
            error: fetchSaleListingsError,
          })
        );
      })
      .catch(e => {
        if (e?.response?.status === 404) {
          dispatch(
            fetchSaleListingsSuccess({
              listings: [],
              pagination: emptyPagination,
              facetCounts: {},
              campaign: null,
              campaignListingPrices: {},
              campaignKey,
              campaignNotFound: true,
            })
          );
          return [];
        }

        dispatch(fetchSaleListingsError(e));
        throw e;
      });
  };
};
